import { useState } from 'react';
import { useWorkspaceStore, pruneUnusedMedia } from '@/store/workspaceStore';
import { ProjectSidebar } from './ProjectSidebar';
import { ReelWorkspace } from './ReelWorkspace';
import { SharedDefaultsPanel } from './SharedDefaultsPanel';
import { BulkQueuePanel } from '../bulk/BulkQueuePanel';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/Modal';

interface DashboardProps {
  darkMode: boolean;
  onToggleDarkMode: () => void;
}

export function Dashboard({ darkMode, onToggleDarkMode }: DashboardProps) {
  const workspace = useWorkspaceStore((s) => s.workspace);
  const debugMode = useWorkspaceStore((s) => s.debugMode);
  const setDebugMode = useWorkspaceStore((s) => s.setDebugMode);
  const storageWarning = useWorkspaceStore((s) => s.storageWarning);
  const clearAllData = useWorkspaceStore((s) => s.clearAllData);
  const createProject = useWorkspaceStore((s) => s.createProject);

  const [mainView, setMainView] = useState<'editor' | 'bulk'>('editor');
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);

  async function handleCleanup() {
    const removed = await pruneUnusedMedia(workspace);
    setCleanupMessage(removed > 0 ? `Removed ${removed} unused media file(s).` : 'No unused media found.');
    setTimeout(() => setCleanupMessage(null), 4000);
  }

  const activeProject = workspace.activeProjectId ? workspace.projects[workspace.activeProjectId] : null;

  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-2 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <h1 className="text-sm font-bold tracking-tight text-slate-800 dark:text-slate-100">🎬 Reels Generator</h1>
          <nav className="flex gap-1 rounded-lg bg-slate-100 p-0.5 text-xs dark:bg-slate-800">
            <button
              onClick={() => setMainView('editor')}
              className={`rounded-md px-3 py-1.5 font-medium ${mainView === 'editor' ? 'bg-white shadow dark:bg-slate-700' : 'text-slate-500'}`}
            >
              Reel editor
            </button>
            <button
              onClick={() => setMainView('bulk')}
              className={`rounded-md px-3 py-1.5 font-medium ${mainView === 'bulk' ? 'bg-white shadow dark:bg-slate-700' : 'text-slate-500'}`}
            >
              Bulk queue
            </button>
          </nav>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Button size="sm" variant="ghost" onClick={() => setDefaultsOpen(true)}>
            Workspace defaults
          </Button>
          <Button size="sm" variant="ghost" onClick={handleCleanup} title="Delete any uploaded media no longer used by any Reel">
            Clean up unused media
          </Button>
          <label className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
            <input type="checkbox" checked={debugMode} onChange={(e) => setDebugMode(e.target.checked)} />
            Debug mode
          </label>
          <Button size="sm" variant="ghost" onClick={onToggleDarkMode} aria-label="Toggle dark mode">
            {darkMode ? '☀️ Light' : '🌙 Dark'}
          </Button>
          <Button size="sm" variant="danger" onClick={() => setConfirmClear(true)}>
            Clear all data
          </Button>
        </div>
      </header>

      {cleanupMessage && (
        <div className="border-b border-brand-200 bg-brand-50 px-4 py-1.5 text-xs text-brand-800 dark:border-brand-900 dark:bg-brand-900/20 dark:text-brand-200">
          {cleanupMessage}
        </div>
      )}

      {storageWarning && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          ⚠ {storageWarning}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <ProjectSidebar />
        <main className="flex-1 overflow-hidden">
          {mainView === 'bulk' ? (
            <div className="h-full overflow-y-auto p-4">
              <BulkQueuePanel />
            </div>
          ) : activeProject ? (
            <ReelWorkspace project={activeProject} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-400">
              <p>No Reel selected yet.</p>
              <Button variant="primary" onClick={() => createProject()}>
                + Create your first Reel
              </Button>
            </div>
          )}
        </main>
      </div>

      <SharedDefaultsPanel open={defaultsOpen} onClose={() => setDefaultsOpen(false)} />
      <ConfirmDialog
        open={confirmClear}
        title="Clear all local data?"
        message="This deletes every Reel, uploaded media, and shared setting stored in this browser. This cannot be undone."
        confirmLabel="Clear everything"
        onCancel={() => setConfirmClear(false)}
        onConfirm={() => {
          void clearAllData();
          setConfirmClear(false);
        }}
      />
    </div>
  );
}
